import React from 'react';
import { YStack, XStack, Button, useTheme, TextArea, Image } from 'tamagui';
import { useNavigate } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';

import { Strings } from '../../../constants';
import CloseIcon from '@mui/icons-material/Close';
import { CobuStack, CobuText, CobuTextAvatar, CobuLoader, CobuHeader, CobuBackButton, CobuView, CobuAlert } from '@cobu/ui';
import { URLS } from '../../../constants';
import useComposeComment from '../../../hooks/useComposeComment';
import { useMarketplaceDetection } from '../../../hooks/useMarketplaceDetection';
import { useParams, useLocation } from 'react-router-dom';
import CollectionsIcon from '@mui/icons-material/Collections';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { useVisualViewportHeight } from '../../../hooks/useVisualViewportHeight';
import { getModerationAlertConfig } from '../../../constants/moderation';
import FlaggedTermsPreview from '../../moderation/FlaggedTermsPreview';
import { useEffect, useState } from 'react';
import { logger } from '../../../utils/logger';

const strings = Strings.get();

export default function ComposeCommentScreen() {
  useVisualViewportHeight();
  const theme = useTheme();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  
  const groupId = params.groupId || search.get('groupId') || undefined;
  
  // Detect if we're in marketplace context
  const { isMarketplace } = useMarketplaceDetection(groupId);
  
  const {
    user,
    replyingTo,
    showReplyingTo,
    contents,
    setContents,
    images,
    removeImageAt,
    pickImage,
    takePhoto,
    isSubmitting,
    handlePost,
    keyboardShown,
    userDisplayName,
    flaggedTerms,
    moderationAlert,
    closeModerationAlert,
    handleModerationDecision,
    marketplaceAlert,
    closeMarketplaceAlert,
    handleMarketplaceDecision,
  } = useComposeComment();
  
  // Use marketplace strings if in marketplace context
  const headerTitle = isMarketplace 
    ? strings.discussion.marketplace.composeHeader 
    : strings.groupDetail.actions.composeComment;
  const placeholder = isMarketplace
    ? strings.discussion.marketplace.composePlaceholder
    : strings.discussion.compose.placeholder;

  const MOBILE_BREAKPOINT = 475;
  const [isSmallScreen, setIsSmallScreen] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.innerWidth <= MOBILE_BREAKPOINT;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleResize = () => setIsSmallScreen(window.innerWidth <= MOBILE_BREAKPOINT);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    logger.log('[ComposeComment] isSmallScreen', isSmallScreen, window.innerWidth);
  }, [isSmallScreen]);

  const renderLoading = () => {
    return (
      <CobuStack
        ai="center"
        jc="center"
        f={1}
        pos="absolute"
        t={0}
        l={0}
        r={0}
        b={0}
        bg="$blackTransparent50"
        zi={1000}
      >
        <CobuLoader />
      </CobuStack>
    );
  };

  return (
    <CobuStack f={1} bg="$white" mih={0} mah={`calc(var(--vvh) - 48px)`}>
      {isSubmitting && renderLoading()}
      <CobuHeader
        title={headerTitle}
        renderLeft={() => (
          <CobuBackButton onPress={() => navigate(-1)} />
        )}
        renderRight={() => (
          <Button
            unstyled
            onPress={handlePost}
            disabled={isSubmitting}
            bg="transparent"
            bw={0}
            boc="transparent"
            hoverStyle={{ bg: 'transparent' }}
            pressStyle={{ bg: 'transparent' }}
            focusStyle={{ outlineWidth: 0 }}
          >
            <CobuText col="$textLinkColor" fontSize="$4" opacity={isSubmitting ? 0.5 : 1}>
              {strings.common.buttonLabels.post}
            </CobuText>
          </Button>
        )}
      />

      <CobuStack f={1}>
        <CobuStack f={1}>
          {showReplyingTo && renderReplyingTo()}
          {renderCommentInput()}
          {renderImageActions()}
        </CobuStack>
      </CobuStack>
      {renderModerationAlert()}
      {renderMarketplaceAlert()}
    </CobuStack>
  );

  function renderReplyingTo() {
    return (
      <CobuStack gap="$2" bg="$white" p="$4">
        <CobuStack layout='horizontal' ai="center" gap="$2">
          {replyingTo?.user?.profileImage &&
            replyingTo?.user?.profileImage !== URLS.DEFAULT_PROFILE_IMAGE ? (
            <Image
              source={{ uri: replyingTo.user.profileImage }}
              w={32}
              h={32}
              br="$4"
              ov="hidden"
              objectFit="cover"
            />
          ) : (
            <CobuTextAvatar
              name={replyingTo?.user?.displayName || strings.ui.members.unknownUser}
              uuid={replyingTo?.user?.uuid || ''}
              size={'$8'}
            />
          )}
          <CobuStack f={1}>
            <CobuText fow="600" col="$color" numberOfLines={1}>
              {replyingTo?.user?.displayName || strings.ui.members.unknownUser}
            </CobuText>
            <CobuText
              col={replyingTo?.removed ? '$colorSecondary' : '$color'}
              numberOfLines={2}
              ellipsizeMode="tail"
            >
              {replyingTo?.removed
                ? strings.discussion.moderation.contentRemoved
                : replyingTo?.contents}
            </CobuText>
          </CobuStack>
        </CobuStack>
      </CobuStack>
    );
  }

  function renderCommentInput() {
    return (
      <CobuStack layout='horizontal' gap="$3" ai="flex-start" px="$4" py="$4" f={1}>
        {user?.profileImage && user?.profileImage !== URLS.DEFAULT_PROFILE_IMAGE ? (
          <Image
            source={{ uri: user?.profileImage }}
            w={36}
            h={36}
            br="$5"
            ov="hidden"
            objectFit="cover"
          />
        ) : (
          <CobuTextAvatar
            name={userDisplayName || strings.ui.members.unknownUser}
            uuid={user?.uuid || ''}
            size={'$8'}
          />
        )}

        <CobuStack f={1}>
          <TextArea
            value={contents}
            onChangeText={setContents}
            placeholder={placeholder}
            placeholderTextColor="$colorMuted"
            col="$color"
            fos="$3"
            p="$3"
            mih={'$10'}
            mah={'60%'}
            bc="transparent"
            borderWidth={0}
            borderColor='$cardBorderColor'
            multiline
            numberOfLines={10}
            verticalAlign="top"
            autoFocus
            scrollEnabled
            autoCapitalize="sentences"
            focusStyle={{
              borderColor: '$black',
              outlineWidth: 0,
              outlineColor: 'transparent',
              shadowColor: 'transparent',
              borderWidth: 0,
            }}
          />

          <FlaggedTermsPreview contents={contents} flaggedTerms={flaggedTerms} />

          {/* Images preview */}
          {images.length > 0 && (
            <CobuStack mt={'$4'} style={{ width: '100%', overflow: 'hidden' }}>
              <Virtuoso
                data={images}
                itemContent={(index, item) => (
                  <CobuStack key={`${item.uri}-${index}`} pos="relative" mr={'$4'} h={88} pt={8}>
                    <Image
                      source={{ uri: item.uri }}
                      w={80}
                      h={80}
                      br="$2"
                      ov="hidden"
                      objectFit="cover"
                    />
                    <Button
                      onPress={() => removeImageAt(index)}
                      pos="absolute"
                      t={-2}
                      r={-8}
                      bg="$blackTransparent50"
                      br={'$3'}
                      h={'$5'}
                      w={'$5'}
                      p={0}
                    >
                      <CloseIcon width={14} height={14} color={theme.white?.val} />
                    </Button>
                  </CobuStack>
                )}
                computeItemKey={(index, item) => `${item.uri}-${index}`}
                horizontalDirection
                style={{ height: 80, width: '100%', overflowY: 'hidden' }}
              />
            </CobuStack>
          )}
        </CobuStack>
      </CobuStack>
    );
  }

  function renderImageActions() {
    return (
      <CobuStack
        layout='horizontal'
        mt={12}
        jc={isSmallScreen ? 'space-evenly' : 'center'}
        gap="$3"
        f={1}
        ai="flex-end"
        py="$4"
      >
        <Button
          onPress={pickImage}
          mih="$8"
          h="auto"
          icon={<CollectionsIcon width={22} height={22} style={{ color: theme.cobuPink?.val }} />}
          bg="transparent"
          bw={'$0.5'}
          boc={'$cardBorderColor'}
          br={'$6'}
          fs={1}
        >
          <CobuText variant="body">
            {strings.ui.imagePicker.openGallery}
          </CobuText>
        </Button>
        {isSmallScreen && (
          <Button
            onPress={takePhoto}
            mih="$8"
            h="auto"
            icon={<PhotoCameraIcon width={22} height={22} style={{ color: theme.cobuPink?.val }} />}
            bg="transparent"
            bw={'$0.5'}
            boc={'$cardBorderColor'}
            br={'$6'}
            fs={1}
          >
            <CobuText variant="body">
              {strings.ui.imagePicker.openCamera}
            </CobuText>
          </Button>
        )}
      </CobuStack>
    );
  }

  function renderAlert(
    open: boolean,
    title: string,
    description: string,
    actions: Array<{ text: string; onPress: () => void; style?: 'default' | 'cancel' | 'destructive' }>,
    onClose: () => void
  ) {
    if (!open) return null;

    return (
      <CobuAlert
        open={open}
        onOpenChange={(isOpen) => !isOpen && onClose()}
        title={title}
        description={description}
        actions={actions}
      />
    );
  }

  function renderModerationAlert() {
    if (!moderationAlert.open || !moderationAlert.type) return null;

    const alertConfig = getModerationAlertConfig(moderationAlert.type);
    const actions = alertConfig.options.map((option) => ({
      text: option.text,
      onPress: () => handleModerationDecision(option.text),
      style: option.style,
    }));

    return renderAlert(
      moderationAlert.open,
      alertConfig.title,
      alertConfig.message,
      actions,
      closeModerationAlert
    );
  }

  function renderMarketplaceAlert() {
    const actions = [
      {
        text: strings.discussion.marketplace.confirmYes,
        onPress: () => handleMarketplaceDecision(strings.discussion.marketplace.confirmYes),
        style: 'default' as const,
      },
      {
        text: strings.discussion.marketplace.confirmNo,
        onPress: () => handleMarketplaceDecision(strings.discussion.marketplace.confirmNo),
        style: 'cancel' as const,
      },
    ];

    return renderAlert(
      marketplaceAlert.open,
      strings.discussion.marketplace.detectedTitle,
      strings.discussion.marketplace.detectedMessage,
      actions,
      closeMarketplaceAlert
    );
  }

}
